import { useCallback, useState } from "react";
import {
  CreateSupportChatPayload,
  SupportChatDto,
  SupportChatsMeta,
  supportApi,
} from "./supportApi";

interface UseSupportChatsReturn {
  chats: SupportChatDto[];
  meta: SupportChatsMeta | null;
  isLoading: boolean;
  error: any;
  fetchChats: (params?: { page?: number; per_page?: number }) => Promise<SupportChatDto[]>;
  createChat: (payload: CreateSupportChatPayload) => Promise<any>;
  closeChat: (chatId: string | number) => Promise<any>;
}

export const useSupportChats = (): UseSupportChatsReturn => {
  const [chats, setChats] = useState<SupportChatDto[]>([]);
  const [meta, setMeta] = useState<SupportChatsMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const fetchChats = useCallback(async (params?: { page?: number; per_page?: number }) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await supportApi.listChats(params);
      const results = resp?.data?.data ?? [];
      const nextMeta = resp?.data?.meta ?? null;
      setChats(Array.isArray(results) ? results : []);
      setMeta(nextMeta);
      return Array.isArray(results) ? results : [];
    } catch (e: any) {
      setError(e);
      setChats([]);
      setMeta(null);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createChat = useCallback(async (payload: CreateSupportChatPayload) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp: any = await supportApi.createChat(payload);
      return resp?.data;
    } catch (e: any) {
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const closeChat = useCallback(async (chatId: string | number) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp: any = await supportApi.closeChat(chatId);
      return resp;
    } catch (e: any) {
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    chats,
    meta,
    isLoading,
    error,
    fetchChats,
    createChat,
    closeChat,
  };
};
